import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AREAS, PROJECT_STATUS } from "@/lib/constants";
import { Pencil, Trash2, Plus, X } from "lucide-react";

const slugify = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const empty = { title: "", subtitle: "", slug: "", cover_image: "", short_desc: "", full_desc: "", area: "famiglie", audience: "", status: "attivo", contact_email: "", featured: false, partners: [], gallery: [], materials: [] };

export default function ProjectsAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/projects").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.title || !editing.slug) return toast.error("Titolo e slug obbligatori");
    try {
      if (editing.id) await api.put(`/admin/projects/${editing.id}`, editing);
      else await api.post("/admin/projects", editing);
      toast.success("Salvato"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const del = async (id) => {
    if (!confirm("Eliminare?")) return;
    await api.delete(`/admin/projects/${id}`);
    toast.success("Eliminato"); load();
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((s) => ({ ...s, cover_image: data.path }));
      toast.success("Caricato");
    } catch { toast.error("Upload fallito"); }
    finally { setUploading(false); }
  };

  const input = "w-full rounded-xl bg-white border border-[#292522]/12 px-3 py-2 focus:border-[#FF6100] focus:ring-2 focus:ring-[#FF6100]/20 outline-none text-sm";

  return (
    <div data-testid="admin-projects">
      <div className="flex items-end justify-between mb-6">
        <div><h1 className="font-display text-4xl font-medium">Progetti</h1><p className="text-sm text-[#292522]/60 mt-1">Archivio progetti e iniziative.</p></div>
        <button onClick={() => setEditing({ ...empty })} className="btn-primary text-sm" data-testid="new-project-btn"><Plus className="h-4 w-4" />Nuovo progetto</button>
      </div>

      <div className="rounded-2xl bg-white border border-[#292522]/8 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8F1E8] text-xs uppercase"><tr><th className="text-left p-3">Titolo</th><th className="text-left p-3">Area</th><th className="text-left p-3">Stato</th><th className="text-left p-3">Evidenza</th><th className="p-3"></th></tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-t border-[#292522]/8">
                <td className="p-3"><div className="font-semibold">{p.title}</div><div className="text-xs text-[#292522]/50">/{p.slug}</div></td>
                <td className="p-3 text-xs">{p.area}</td>
                <td className="p-3"><span className="rounded-full bg-[#FFF1E8] text-[#FF6100] font-semibold text-xs px-2 py-0.5">{p.status}</span></td>
                <td className="p-3 text-xs">{p.featured ? "★" : ""}</td>
                <td className="p-3 text-right">
                  <button onClick={() => setEditing({ ...empty, ...p })} className="p-1.5 hover:text-[#FF6100]"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(p.id)} className="p-1.5 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-[#292522]/50 flex items-start md:items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-2xl my-8">
            <div className="flex items-center justify-between p-5 border-b border-[#292522]/8">
              <h2 className="font-display text-2xl font-medium">{editing.id ? "Modifica" : "Nuovo"} progetto</h2>
              <button onClick={() => setEditing(null)}><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              <label className="block"><span className="text-xs font-semibold">Titolo *</span>
                <input className={input} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value, slug: editing.slug || slugify(e.target.value) })} />
              </label>
              <div className="grid sm:grid-cols-2 gap-3">
                <label><span className="text-xs font-semibold">Slug *</span><input className={input} value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: slugify(e.target.value) })} /></label>
                <label><span className="text-xs font-semibold">Sottotitolo</span><input className={input} value={editing.subtitle} onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })} /></label>
                <label><span className="text-xs font-semibold">Area</span>
                  <select className={input} value={editing.area} onChange={(e) => setEditing({ ...editing, area: e.target.value })}>{AREAS.map((a) => <option key={a.key} value={a.key}>{a.short}</option>)}</select>
                </label>
                <label><span className="text-xs font-semibold">Stato</span>
                  <select className={input} value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>{PROJECT_STATUS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}</select>
                </label>
                <label className="col-span-2"><span className="text-xs font-semibold">Pubblico</span><input className={input} value={editing.audience} onChange={(e) => setEditing({ ...editing, audience: e.target.value })} /></label>
                <label className="col-span-2"><span className="text-xs font-semibold">Descrizione breve</span><textarea rows="2" className={input} value={editing.short_desc} onChange={(e) => setEditing({ ...editing, short_desc: e.target.value })} /></label>
                <label className="col-span-2"><span className="text-xs font-semibold">Descrizione completa</span><textarea rows="5" className={input} value={editing.full_desc} onChange={(e) => setEditing({ ...editing, full_desc: e.target.value })} /></label>
                <label className="col-span-2"><span className="text-xs font-semibold">Contatto email</span><input className={input} value={editing.contact_email} onChange={(e) => setEditing({ ...editing, contact_email: e.target.value })} /></label>
                <label className="col-span-2"><span className="text-xs font-semibold">Copertina</span>
                  <input type="file" accept="image/*" onChange={(e) => upload(e.target.files[0])} disabled={uploading} className="text-xs" />
                  {editing.cover_image && <p className="text-xs text-[#216B65] mt-1">✓ {editing.cover_image}</p>}
                </label>
                <label className="col-span-2 flex items-center gap-2"><input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} className="accent-[#FF6100]" /><span className="text-xs font-semibold">In evidenza</span></label>
              </div>
            </div>
            <div className="p-5 border-t flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="btn-ghost text-sm">Annulla</button>
              <button onClick={save} className="btn-primary text-sm">Salva</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
